/**
 * sanitiser.ts — Pulse content sanitiser (Week 1 of the sandbox).
 *
 * Every byte the Composer agent writes into a Zeqzone passes through this
 * function. The contract is simple: if a tag, attribute, or attribute value
 * could escape the zone, take over the page, exfiltrate data, or steal a
 * locked element's identity, we strip it before the DOM ever sees it.
 *
 * This is layer 2 of the three-layer sandbox spec'd in Zeq Orb v1 §6.
 *   Layer 1: HTML attributes (data-zeq-locked / data-zeq-zone)
 *   Layer 2: this file — content sanitisation pre-write
 *   Layer 3: CSS containment (containment.css)
 *
 * Design rules:
 *   - Allowlist by default for tags. Anything we haven't approved is dropped.
 *   - Block-list for attributes — easier to enumerate dangerous ones.
 *   - URLs allow http(s)://, mailto:, tel:, and same-origin relative paths.
 *     javascript: and data: URIs are nuked unconditionally.
 *   - We never mutate the input string in place. Sanitise = parse → rebuild.
 *   - Returns the cleaned HTML and a list of removed-tags / removed-attrs so
 *     rejection-log.ts can post a useful audit row when something was stripped.
 */

/** Tags the agent may emit. Anything else is dropped (children retained). */
const ALLOWED_TAGS = new Set<string>([
  // Block-level
  "div", "section", "article", "aside", "main", "nav", "header", "footer",
  "p", "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "dl", "dt", "dd",
  "blockquote", "figure", "figcaption", "pre",
  // Inline
  "a", "span", "strong", "em", "code", "kbd", "samp", "var", "small",
  "sub", "sup", "mark", "abbr", "cite", "time", "br", "hr", "wbr",
  // Interactive — controlled subset (see ALLOWED_INTERACTIVE_TAGS)
  "button", "label",
  // Tables
  "table", "thead", "tbody", "tfoot", "tr", "th", "td", "caption", "colgroup", "col",
  // Media (with restricted attrs)
  "img", "svg", "path", "circle", "rect", "line", "polyline", "polygon",
  "g", "defs", "linearGradient", "radialGradient", "stop", "use", "text", "tspan",
  "ellipse", "title", "desc",
  // Pulse components — namespaced custom elements rendered by Zone API
  // (allowed as inert hosts; the Zone API hydrates them after sanitise.)
  "zeq-readout", "zeq-chart", "zeq-proof-trail", "zeq-audit-clock",
  "zeq-state-explorer", "zeq-linktree", "zeq-inbox", "zeq-data-table",
]);

/** Tags that are dropped along with their entire subtree (no child rescue). */
const BLOCKED_TAGS_HARD_DROP = new Set<string>([
  "script", "style", "link", "meta", "iframe", "frame", "frameset",
  "object", "embed", "applet", "base", "head", "html", "body",
  "noscript", "template",
  // Form elements — interactive in ways we don't yet validate
  "form", "input", "textarea", "select", "option", "optgroup", "fieldset", "legend",
]);

/** Attribute names that are always blocked, regardless of tag. */
const BLOCKED_ATTRS = new Set<string>([
  // Event handlers — every one of them
  "onabort", "onafterprint", "onbeforeprint", "onbeforeunload", "onblur",
  "oncanplay", "oncanplaythrough", "onchange", "onclick", "oncontextmenu",
  "oncopy", "oncut", "ondblclick", "ondrag", "ondragend", "ondragenter",
  "ondragleave", "ondragover", "ondragstart", "ondrop", "ondurationchange",
  "onemptied", "onended", "onerror", "onfocus", "onformdata", "onhashchange",
  "oninput", "oninvalid", "onkeydown", "onkeypress", "onkeyup", "onlanguagechange",
  "onload", "onloadeddata", "onloadedmetadata", "onloadstart", "onmessage",
  "onmessageerror", "onmousedown", "onmouseenter", "onmouseleave",
  "onmousemove", "onmouseout", "onmouseover", "onmouseup", "onoffline", "ononline",
  "onpagehide", "onpageshow", "onpaste", "onpause", "onplay", "onplaying",
  "onpointercancel", "onpointerdown", "onpointerenter", "onpointerleave",
  "onpointermove", "onpointerout", "onpointerover", "onpointerup",
  "onpopstate", "onprogress", "onratechange", "onreset", "onresize",
  "onscroll", "onsearch", "onseeked", "onseeking", "onselect",
  "onshow", "onstalled", "onstorage", "onsubmit", "onsuspend",
  "ontimeupdate", "ontoggle", "ontouchcancel", "ontouchend", "ontouchmove",
  "ontouchstart", "onunload", "onvolumechange", "onwaiting", "onwheel",
  // Pulse identity — agent must NEVER add these
  "data-zeq-locked",
  // ID shadowing — agent stealing a locked element's id would let it be
  // grabbed by querySelector and break Layer 1's locked-element protection.
  "id",
  // srcdoc on iframe is moot (iframe is hard-dropped), but defence in depth.
  "srcdoc",
  // Form hijack vectors
  "formaction", "action", "formmethod", "method", "formenctype", "formtarget",
  // Behavioural sinks
  "ping", "downloadurl",
]);

/** Attribute-value patterns that must be rejected. */
const URL_SAFE_PROTOCOLS = /^(?:https?:|mailto:|tel:|sms:|#|\/|\.\/|\.\.\/)/i;
const INLINE_JS_PROTOCOL = /^(?:javascript|vbscript|data|file):/i;

/** Result envelope so callers can audit-log what was removed. */
export interface SanitiseResult {
  html: string;
  /** Tags that were dropped with subtree (script, iframe, etc.). */
  tagsHardDropped: string[];
  /** Tags that were unwrapped (children kept, opening/closing tag removed). */
  tagsUnwrapped: string[];
  /** Attributes that were stripped, as `tag.attr` strings. */
  attrsStripped: string[];
}

/**
 * Sanitise an HTML string. Returns a cleaned HTML string plus a
 * structured report of what was stripped. The report is suitable for
 * forwarding to rejection-log.ts.
 *
 * Implementation note: we use the browser's own DOMParser to parse, then
 * walk the resulting tree applying the rules above. This is dramatically
 * safer than regex sanitising — the parser already canonicalises every
 * gotcha (entity-escaped tags, bogus comments, attribute quoting weirdness).
 *
 * Caller MUST run this in a browser context. For server-side use a
 * different pipeline (server-side rendering of Zone components is done
 * directly by the Zone API and never accepts agent HTML).
 */
export function sanitise(input: string): SanitiseResult {
  const result: SanitiseResult = {
    html: "",
    tagsHardDropped: [],
    tagsUnwrapped: [],
    attrsStripped: [],
  };
  if (!input || typeof input !== "string") return result;

  // Parse in a detached document so layout/script never executes.
  const doc = new DOMParser().parseFromString(
    `<!DOCTYPE html><html><body><div id="__pulse_root__">${input}</div></body></html>`,
    "text/html",
  );
  const root = doc.getElementById("__pulse_root__");
  if (!root) return result;

  walk(root, result);

  result.html = root.innerHTML;
  return result;
}

function walk(node: Element, result: SanitiseResult): void {
  // Iterate children in reverse so removals don't shift the live list.
  const children = Array.from(node.children);
  for (let i = children.length - 1; i >= 0; i--) {
    const el = children[i] as Element;
    const tag = el.tagName.toLowerCase();

    if (BLOCKED_TAGS_HARD_DROP.has(tag)) {
      result.tagsHardDropped.push(tag);
      el.remove();
      continue;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown tag — unwrap (keep children, drop the tag itself).
      result.tagsUnwrapped.push(tag);
      const parent = el.parentNode!;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      // After unwrap, the children we just hoisted still need walking, so
      // re-walk this node (now containing the unwrapped contents).
      walk(node, result);
      return;
    }

    // Allowed tag — sanitise its attributes.
    sanitiseAttributes(el, tag, result);

    // Recurse into children.
    walk(el, result);
  }
}

function sanitiseAttributes(el: Element, tag: string, result: SanitiseResult): void {
  const attrs = Array.from(el.attributes);
  for (const a of attrs) {
    const name = a.name.toLowerCase();
    const val = a.value || "";

    // Always-blocked attributes (event handlers, locked-attr injection, id shadowing).
    if (BLOCKED_ATTRS.has(name)) {
      result.attrsStripped.push(`${tag}.${name}`);
      el.removeAttribute(a.name);
      continue;
    }

    // data-zeq-zone declaration is reserved for the page author. Agents
    // cannot create new zones from inside other zones — that would let
    // them grant themselves permissions. Strip it.
    if (name === "data-zeq-zone" || name === "data-zeq-permissions") {
      result.attrsStripped.push(`${tag}.${name}`);
      el.removeAttribute(a.name);
      continue;
    }

    // URL-bearing attributes — refuse javascript: / vbscript: / data: / file:
    if (name === "href" || name === "src" || name === "xlink:href" || name === "action") {
      if (val.length > 0 && INLINE_JS_PROTOCOL.test(val.trim())) {
        result.attrsStripped.push(`${tag}.${name}=<inline-protocol>`);
        el.removeAttribute(a.name);
        continue;
      }
      // For anchors, only allow safe protocols.
      if (tag === "a" && name === "href" && val.length > 0 && !URL_SAFE_PROTOCOLS.test(val.trim())) {
        result.attrsStripped.push(`${tag}.href=<unsafe-scheme>`);
        el.removeAttribute(a.name);
        continue;
      }
    }

    // style attribute — block known escape vectors. We do NOT do a full
    // CSS parse here; Layer 3 (containment.css) provides the real
    // isolation. We just strip the obvious sins.
    if (name === "style" && val.length > 0) {
      const lower = val.toLowerCase();
      if (
        lower.includes("position:fixed") ||
        lower.includes("position: fixed") ||
        lower.includes("position:absolute") ||  // absolute can escape contain:layout
        lower.includes("position: absolute") ||
        lower.includes("z-index") ||
        lower.includes("expression(") ||
        lower.includes("url(javascript:") ||
        lower.includes("@import")
      ) {
        result.attrsStripped.push(`${tag}.style=<containment-escape>`);
        el.removeAttribute(a.name);
        continue;
      }
    }

    // target="_blank" with rel — force noopener/noreferrer on link agents.
    if (tag === "a" && name === "target" && val === "_blank") {
      const existingRel = (el.getAttribute("rel") || "").toLowerCase();
      const required = ["noopener", "noreferrer"];
      const merged = new Set([...existingRel.split(/\s+/).filter(Boolean), ...required]);
      el.setAttribute("rel", Array.from(merged).join(" "));
    }
  }
}

/**
 * Public helper — does the input contain anything the sanitiser would strip?
 * Used by ZeqZone.write() to short-circuit when there's nothing to clean.
 */
export function isSanitiseTrivial(input: string): boolean {
  return !/<[a-z]/i.test(input) && !/on[a-z]+\s*=/i.test(input);
}
